import React, { useEffect, useState } from "react";
import { Truck, Trash2 } from "lucide-react";
import { Input, Popconfirm } from "antd";
import { useLocation } from "react-router-dom";

import {
  useAllOrders,
  useCancelOrder,
} from "../../../../../api/orders/orders";
import CourierModal from "../../../../../component/CourierModal";
import { generateCourierPayload } from "../../../../../utils/helpers";

const ORDERS_PER_PAGE = 20;

// 🚀 Map UI tab → backend numeric shippingStatus codes
const STATUS_MAP = {
  pendings: "1",
  pending: "1",
  ready_to_ship: "2",
  picklist: "3",
  shipped: "7",
  delivered: "9",
  cancelled: "5",
};

const Shipping = ({ status = "pendings" }) => {
  const location = useLocation();
  const newOrderId = location.state?.newOrderId || null;

  const [modalVisible, setModalVisible] = useState(!!newOrderId);
  const [selectedOrderId, setSelectedOrderId] = useState(newOrderId);
  const [selectedOrderData, setSelectedOrderData] = useState(null);
  const [courierPayload, setCourierPayload] = useState(null);

  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Debounce search
  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
      setCurrentPage(1); // Reset to page 1 when search changes
    }, 400);
    return () => clearTimeout(delay);
  }, [searchText]);

  // Fetch orders with search parameter passed to API
  const { data, isLoading, refetch } = useAllOrders(
    {
      currentPage,
      ORDERS_PER_PAGE,
      status,
      search: debouncedSearchText, // Pass search to API
    },
    {
      enabled: true,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  const { mutate: cancelOrder } = useCancelOrder();

  // Get orders and pagination info from API
  const orders = data?.orders || [];
  const totalPages = data?.totalPages || 1;
  const totalOrders = data?.totalOrders || 0;

  const openCourierModal = (order) => {
    setSelectedOrderId(order._id);
    setSelectedOrderData(order);
    const payload = generateCourierPayload(order);
    setCourierPayload(payload);
    setModalVisible(true);
  };

  return (
    <div className="w-full mt-6">
      {/* Search Box */}
      <div className="mb-4 flex justify-between items-center">
        <Input
          className="w-80"
          placeholder="Search by AWB, Order Number or Name"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        {totalOrders > 0 && (
          <div className="text-sm text-gray-600">
            Total Orders: <strong>{totalOrders}</strong>
          </div>
        )}
      </div>

      {/* Orders Table */}
      <div className="overflow-x-auto hide-scrollbar">
        <table className="min-w-[1000px] w-full table-auto border border-gray-200">
          <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
            <tr>
              <th className="p-3">Order Date</th>
              <th className="p-3">Order ID / Status</th>
              <th className="p-3">Payment</th>
              <th className="p-3">Products</th>
              <th className="p-3">Customer Name / Contact</th>
              <th className="p-3">Address</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white text-sm">
            {isLoading ? (
              <tr>
                <td colSpan="7">
                  <div className="flex justify-center items-center py-10 text-gray-500 space-x-2">
                    <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
                    <span>Loading orders...</span>
                  </div>
                </td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-6 text-gray-400">
                  {debouncedSearchText
                    ? "No orders found matching your search."
                    : "No orders found."}
                </td>
              </tr>
            ) : (
              orders.map((order, index) => (
                <tr key={order._id || index} className="hover:bg-gray-50">
                  <td className="p-4">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>

                  <td className="p-4">
                    <div className="font-medium text-blue-600">
                      {order.OrderNumber}
                    </div>

                    {/* Status Badge */}
                    <div
                      className={`text-xs font-semibold px-2 py-1 rounded inline-block mt-1 ${
                        order.shippingStatusText === "cancelled"
                          ? "bg-red-100 text-red-600"
                          : order.shippingStatusText === "Ready to Ship"
                          ? "bg-green-100 text-green-600"
                          : "bg-yellow-100 text-yellow-600"
                      }`}
                    >
                      {order.shippingStatusText}
                    </div>
                  </td>

                  <td className="p-4">
                    <p>₹{order.InvoiceAmount}</p>
                    <span className="inline-block text-xs bg-green-100 text-green-700 px-2 py-1 rounded mt-1">
                      {order.PaymentType}
                    </span>
                  </td>

                  <td className="p-4">
                    {order.ProductDetails?.map((p, i) => (
                      <p key={i} className="truncate">{p.name}</p>
                    ))}
                  </td>

                  <td className="p-4">
                    <p>{order.CustomerName}</p>
                    <p>{order.BillingAddress?.Contact}</p>
                  </td>

                  <td className="p-4 text-sm">
                    <p>{order.BillingAddress?.City}</p>
                    <p>{order.BillingAddress?.State}</p>
                    <p>{order.BillingAddress?.Pincode}</p>
                  </td>

                  <td className="p-4 flex space-x-2">
                    {/* Courier Button */}
                    <button
                      className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded"
                      onClick={() => openCourierModal(order)}
                    >
                      <Truck size={16} />
                    </button>

                    {/* Cancel Button */}
                    <Popconfirm
                      title="Cancel this order?"
                      onConfirm={() => cancelOrder(order.shiprocket_order_id)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <button className="bg-red-500 hover:bg-red-600 text-white p-2 rounded">
                        <Trash2 size={16} />
                      </button>
                    </Popconfirm>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalOrders > 0 && (
        <div className="flex justify-between items-center mt-6 text-sm text-gray-700">
          <p>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
            {" "}({totalOrders} total orders)
          </p>

          <div className="space-x-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1 || isLoading}
              className={`px-4 py-2 rounded border ${
                currentPage === 1 || isLoading
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Previous
            </button>

            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages || isLoading}
              className={`px-4 py-2 rounded border ${
                currentPage === totalPages || isLoading
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Courier Modal */}
      {modalVisible && (
        <CourierModal
          orderId={selectedOrderId}
          order={selectedOrderData}
          onClose={() => setModalVisible(false)}
          onShipped={() => {
            setModalVisible(false);
            refetch();
          }}
        />
      )}
    </div>
  );
};

export default Shipping;